"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function StartYourTransformation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="61615" id="submit-a-referral" ref={ref} className={`bg-accent/40 text-foreground py-24 px-4 transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="max-w-[600px] mx-auto w-full">
        <div className="flex flex-col items-center mb-12">
          <span className="bg-primary/10 text-primary px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
            Secure Referral Portal
          </span>
          <h2 className="text-[32px] font-bold text-center font-sans tracking-tight uppercase text-primary">
            Physician Referral Portal
          </h2>
        </div>

        <FormProvider formKey="referral_form_physicians" formId="referral_form_physicians" sectionName="submit-a-referral" defaultValues={{
        full_name: "",
        email: "",
        phone: "",
        current_weight: "",
        goal_weight: "",
        message: ""
      }} validationRules={{
        full_name: {
          required: "Physician Name is required"
        },
        email: {
          required: "Practice Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid email address"
          }
        },
        phone: {
          required: "Practice Phone Number is required",
          pattern: {
            value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
            message: "Invalid phone number"
          }
        },
        current_weight: {
          min: {
            value: 50,
            message: "Minimum weight is 50 lb"
          },
          max: {
            value: 500,
            message: "Maximum weight is 500 lb"
          }
        },
        goal_weight: {
          min: {
            value: 30,
            message: "Minimum weight is 30 lb"
          },
          max: {
            value: 500,
            message: "Maximum weight is 500 lb"
          }
        }
      }} submitText="Submit Secure Referral" successMessage="Thank you. The referral has been received securely. Our clinical team will contact the patient and provide updates to your office." errorMessage="There was a problem submitting the referral. Please contact our office directly.">
          <TransformationForm />
        </FormProvider>
      </div>
    </section>;
}
function TransformationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-card border border-border text-center animate-in fade-in zoom-in duration-500 rounded-lg shadow-sm">
        <h3 className="text-xl font-bold mb-2 text-primary">Referral Received</h3>
        <p className="text-muted-foreground">{successMessage}</p>
      </div>;
  }
  const inputClasses = "bg-transparent border-0 border-b border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-secondary h-12 text-base placeholder:text-muted-foreground/30 transition-colors";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="full_name" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm font-bold uppercase tracking-wide text-primary">Referring Physician Name</FormLabel>
            <FormControl>
              <Input placeholder="Dr. Jane Smith" className={inputClasses} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sm font-bold uppercase tracking-wide text-primary">
                Practice Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="office@practice.com" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sm font-bold uppercase tracking-wide text-primary">
                Practice Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="current_weight" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sm font-bold uppercase tracking-wide text-primary">
                Patient Current Weight (lb)
              </FormLabel>
              <FormControl>
                <Input type="number" placeholder="180" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="goal_weight" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sm font-bold uppercase tracking-wide text-primary">
                Patient Goal Weight (lb)
              </FormLabel>
              <FormControl>
                <Input type="number" placeholder="150" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm font-bold uppercase tracking-wide text-primary">
              Clinical Notes & Referral Reason
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Please include patient symptoms, sleep study results, and any relevant medical history..." className={`${inputClasses} min-h-[100px] resize-none py-3`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-md">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-lg bg-secondary text-secondary-foreground hover:bg-secondary/90 text-base font-bold uppercase tracking-widest shadow-lg shadow-secondary/20 transition-all">
        {isSubmitting ? <>
            <Loader className="mr-2 h-4 w-4 animate-spin" />
            Processing Securely...
          </> : "Submit Secure Referral"}
      </Button>
    </Form>;
}